// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import DataTable from "@insite/mobius/DataTable";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";

interface Props {
    attributeTypes?: AttributeTypeModel[];
    specifications?: SpecificationModel[];
    brandName?: string;
    splitTable?: boolean;
}

type AttributesSpecificationsSectionStyles = {
    wrapper?: InjectableCss;
    section?: InjectableCss;
    table?: InjectableCss;
    tableContainer?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    valueCell?: InjectableCss;
    labelCell?: InjectableCss;
};

export const attributesSpecificationsSectionStyles: AttributesSpecificationsSectionStyles = {};
const styles = attributesSpecificationsSectionStyles;

type Row = { key: string; value: any };

const AttributesSpecificationsSection: FC<Props> = ({
    attributeTypes = [],
    specifications = [],
    brandName,
    splitTable,
}) => {
    const mappedAttributes =
        attributeTypes
            .filter(attributeType => attributeType.includeOnProduct)
            .map(({ label, attributeValues }) => ({
                key: label,
                value: attributeValues?.map(({ valueDisplay }) => valueDisplay)?.join(", ") || "",
            })) || [];
    if (brandName) {
        mappedAttributes.push({
            key: translate("Brand"),
            value: brandName,
        });
    }

    const mappedSpecifications = specifications.map(specification => ({
        key: specification.nameDisplay || specification.name,
        value: parse(transformContentHtml(specification.htmlContent), parserOptions),
    }));
    let attributeTableRows1: Row[] = [];
    let attributeTableRows2: Row[] = [];
    let specificationsTableRows1: Row[] = [];
    let specificationsTableRows2: Row[] = [];
    if (splitTable) {
        const halfAttributes = Math.ceil(mappedAttributes.length / 2) + 1;
        attributeTableRows1 = mappedAttributes.slice(0, halfAttributes);
        attributeTableRows2 = mappedAttributes.slice(halfAttributes);
        const halfSpecifications = Math.ceil(mappedSpecifications.length / 2) + 1;
        specificationsTableRows1 = mappedSpecifications.slice(0, halfSpecifications);
        specificationsTableRows2 = mappedSpecifications.slice(halfSpecifications);
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            {!!mappedAttributes?.length && (
                <StyledWrapper
                    {...styles.section}
                    {...{ hasDivider: !!mappedAttributes.length && !!specifications?.length }}
                >
                    {splitTable ? (
                        <>
                            {!!attributeTableRows1.length && <KeyValueTableSection rows={attributeTableRows1} />}
                            {!!attributeTableRows2.length && <KeyValueTableSection rows={attributeTableRows2} />}
                        </>
                    ) : (
                        <KeyValueTableSection rows={mappedAttributes} />
                    )}
                </StyledWrapper>
            )}

            {!!mappedSpecifications?.length && (
                <StyledWrapper {...styles.section}>
                    {splitTable ? (
                        <>
                            {!!specificationsTableRows1.length && (
                                <KeyValueTableSection rows={specificationsTableRows1} />
                            )}
                            {!!specificationsTableRows2.length && (
                                <KeyValueTableSection rows={specificationsTableRows2} />
                            )}
                        </>
                    ) : (
                        <KeyValueTableSection rows={mappedSpecifications} />
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default AttributesSpecificationsSection;

const KeyValueTableSection = ({ rows }: { rows: Row[] }) => {
    return (
        <StyledWrapper {...styles.tableContainer}>
            <DataTable {...styles.table}>
                <DataTableBody>
                    {rows.map(({ key, value }) => (
                        <DataTableRow key={key}>
                            <DataTableCell {...styles.labelCell}>
                                <Typography {...styles.label}>{key}</Typography>
                            </DataTableCell>
                            <DataTableCell {...styles.valueCell}>
                                <Typography {...styles.value}>
                                    {value}
                                </Typography>
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};
